/**
 * PDP Gallery Grid - Custom Element
 * Handles thumbnail click to open product media modal
 * Maintains zoom functionality from existing modal
 */

if (!customElements.get('pdp-gallery-grid')) {
  customElements.define(
    'pdp-gallery-grid',
    class PdpGalleryGrid extends HTMLElement {
      constructor() {
        super();
        this.mainImageContainer = null;
        this.thumbnails = [];
        this.allImagesData = [];
        this.currentIndex = 0;
        this.totalImages = 0;
        this.expanded = false;
      }

      connectedCallback() {
        this.init();
        this.bindEvents();
        this.initMobileSwiper();
      }

      init() {
        this.mainImageContainer = this.querySelector('.pdp-gallery-grid__main');
        this.thumbnails = this.querySelectorAll('.pdp-gallery-grid__thumb:not(.pdp-gallery-grid__thumb--empty)');
        this.totalImages = parseInt(this.dataset.totalImages) || 0;
        
        // Parse all images data from script tags
        const scriptTags = this.querySelectorAll('.pdp-gallery-grid__all-images script');
        scriptTags.forEach((script) => {
          this.allImagesData.push({
            mediaId: String(script.dataset.mediaId).trim(),
            imgSrc: script.dataset.imgSrc,
            alt: script.dataset.alt || '',
            html: script.innerHTML
          });
        });
      }

      bindEvents() {
        // Refresh thumbnails NodeList
        this.thumbnails = this.querySelectorAll('.pdp-gallery-grid__thumb:not(.pdp-gallery-grid__thumb--empty)');

        // Thumbnail click events (avoid double-binding)
        this.thumbnails.forEach((thumb, index) => {
          if (thumb.dataset.pgBound) return;
          thumb.addEventListener('click', () => this.onThumbnailClick(thumb, index));
          thumb.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.onThumbnailClick(thumb, index);
            }
          });
          thumb.dataset.pgBound = '1';
        });

        // Keyboard navigation on main image (bind once)
        if (!this._keyboardBound) {
          this.mainImageContainer?.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
              this.navigateImage(-1);
            } else if (e.key === 'ArrowRight') {
              this.navigateImage(1);
            }
          });
          this._keyboardBound = true;
        }
        
        // Tablet zoom button click event
        const tabletZoomBtn = this.querySelector('.pdp-gallery-grid__tablet-zoom-btn');
        if (tabletZoomBtn && !tabletZoomBtn.dataset.pgBound) {
          tabletZoomBtn.addEventListener('click', () => {
            // Open modal at first slide (main image)
            const mainImage = this.querySelector('.pdp-gallery-grid__main-image');
            const mediaId = mainImage?.dataset.mediaId || '';
            this.openModal(mediaId, 0);
          });
          tabletZoomBtn.dataset.pgBound = '1';
        }
      }

      onThumbnailClick(thumb, index) {
        // Check if clicking on "+N" overlay - expand inline thumbnails
        const hasMoreOverlay = thumb.querySelector('.pdp-gallery-grid__more-overlay');
        if (hasMoreOverlay) {
          this.expandThumbnails();
          return;
        }

        // Get the media ID from the thumbnail
        const mediaId = thumb.dataset.mediaId;
        
        // Calculate the correct modal slide index
        // Modal order: main image (0), then thumbnails (1, 2, 3...)
        // Thumbnails in grid skip the main image, so thumbnail index 0 = modal index 1
        const modalIndex = index + 1;
        
        this.currentIndex = modalIndex;
        this.currentMediaId = mediaId;

        // Open the product media modal with the clicked image
        this.openModal(mediaId, modalIndex);
      }

      navigateImage(direction) {
        const newIndex = this.currentIndex + direction;
        if (newIndex >= 0 && newIndex < this.thumbnails.length) {
          const thumb = this.thumbnails[newIndex];
          if (thumb) {
            this.onThumbnailClick(thumb, newIndex);
          }
        }
      }

      openModal(mediaId, index) {
        // Find the modal and open it at the specific slide
        const sectionId = this.dataset.sectionId || '';
        const modal = document.querySelector(`#CustomProductModal-${sectionId}`);
        
        if (modal) {
          // Create a fake opener element with the media-id to pass to the modal
          const fakeOpener = document.createElement('button');
          fakeOpener.setAttribute('data-media-id', mediaId || '');
          fakeOpener.setAttribute('data-slide-index', (index !== undefined ? index + 1 : 1).toString());
          
          // Show modal
          modal.show(fakeOpener);
        } else {
          // Fallback: trigger existing modal open functionality via main image
          const modalOpener = this.mainImageContainer?.querySelector('.product__media-toggle');
          if (modalOpener) {
            modalOpener.click();
          }
        }
      }

      expandThumbnails() {
        if (this.expanded) return;
        // Append remaining thumbnails inline (use data from allImagesData)
        const container = this.querySelector('.pdp-gallery-grid__thumbnails');
        if (!container) return;

        const sectionId = this.dataset.sectionId || '';

        // Build a set of existing media ids to avoid duplicates
        const existing = new Set();
        
        // Include the main image media id to exclude it from remaining
        const mainImage = this.querySelector('.pdp-gallery-grid__main-image');
        if (mainImage) {
          const mainMediaId = mainImage.dataset.mediaId || '';
          existing.add(String(mainMediaId.replace(sectionId + '-', '')).trim());
        }
        
        this.querySelectorAll('.pdp-gallery-grid__thumb').forEach((t) => {
          const mid = t.dataset.mediaId || '';
          // store plain id if prefixed
          existing.add(String(mid.replace(sectionId + '-', '')).trim());
        });

        // Find remaining images from allImagesData (excluding main image and visible thumbnails)
        const remaining = this.allImagesData.filter((d) => !existing.has(String(d.mediaId).trim()));
        
        // If there is a +N tile, just remove the overlay - the thumbnail already shows the correct image
        const moreThumb = container.querySelector('.pdp-gallery-grid__thumb--more');
        if (moreThumb) {
          // remove --more class and overlay to restore normal visuals
          moreThumb.classList.remove('pdp-gallery-grid__thumb--more');
          const overlay = moreThumb.querySelector('.pdp-gallery-grid__more-overlay');
          if (overlay) overlay.remove();
        }

        // Append remaining thumbnails inline (use data from allImagesData)
        remaining.forEach((data, idx) => {
          const wrapper = document.createElement('div');
          wrapper.className = 'pdp-gallery-grid__thumb';
          wrapper.setAttribute('data-media-id', `${sectionId}-${data.mediaId}`);
          wrapper.setAttribute('data-index', String(this.thumbnails.length + idx));
          wrapper.setAttribute('tabindex', '0');
          wrapper.setAttribute('role', 'button');

          const inner = document.createElement('div');
          inner.className = 'pdp-gallery-grid__thumb-inner';

          // Prefer to parse HTML from the hidden template to preserve markup/alt
          let added2 = false;
          if (data.html) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(data.html, 'text/html');
            const mainImg = doc.querySelector('.pdp-gallery-grid__main-image img, img');
            if (mainImg) {
              const img = mainImg.cloneNode(true);
              img.classList.add('pdp-gallery-grid__thumb-img');
              if (!img.alt) img.alt = data.alt || '';
              inner.appendChild(img);
              added2 = true;
            }
          }

          // Fallback to using data.imgSrc
          if (!added2) {
            const img = document.createElement('img');
            img.className = 'pdp-gallery-grid__thumb-img';
            img.src = data.imgSrc;
            img.alt = data.alt || '';
            inner.appendChild(img);
          }

          wrapper.appendChild(inner);
          container.appendChild(wrapper);
        });

        // Remove any +N overlay(s)
        container.querySelectorAll('.pdp-gallery-grid__more-overlay').forEach((o) => o.remove());

        // mark expanded so we don't append again
        this.expanded = true;

        // Rebind events for new thumbnails (bindEvents will skip already bound elements)
        this.bindEvents();
      }

      // ================================
      // MOBILE SWIPER METHODS
      // ================================
      
      initMobileSwiper() {
        this.mobileSwiper = this.querySelector('.pdp-gallery-grid__mobile-swiper');
        if (!this.mobileSwiper) return;
        
        this.mobileSlides = this.mobileSwiper.querySelector('.pdp-gallery-grid__mobile-slides');
        this.mobileProgressBar = this.mobileSwiper.querySelector('.pdp-gallery-grid__mobile-progress-bar');
        this.mobileProgress = this.mobileSwiper.querySelector('.pdp-gallery-grid__mobile-progress');
        this.mobileCounterCurrent = this.mobileSwiper.querySelector('.pdp-gallery-grid__mobile-counter-current');
        this.mobileZoomBtn = this.mobileSwiper.querySelector('.pdp-gallery-grid__mobile-zoom-btn');
        this.mobileSlidesArray = Array.from(this.mobileSwiper.querySelectorAll('.pdp-gallery-grid__mobile-slide'));
        
        this.mobileTotalSlides = this.mobileSlidesArray.length || 1;
        this.mobileCurrentSlide = 0;
        
        if (!this.mobileSlides) return;
        
        // Remove old listeners before adding new ones (prevent duplicates)
        if (this._mobileScrollHandler) {
          this.mobileSlides.removeEventListener('scroll', this._mobileScrollHandler);
        }
        if (this._mobileZoomHandler && this.mobileZoomBtn) {
          this.mobileZoomBtn.removeEventListener('click', this._mobileZoomHandler);
        }
        
        // Create bound handlers
        this._mobileScrollHandler = this.onMobileScroll.bind(this);
        this._mobileZoomHandler = this.onMobileZoomClick.bind(this);
        
        // Bind scroll event for smooth progress bar
        this.mobileSlides.addEventListener('scroll', this._mobileScrollHandler, { passive: true });
        
        // Bind zoom button click
        if (this.mobileZoomBtn) {
          this.mobileZoomBtn.addEventListener('click', this._mobileZoomHandler);
        }
      }
      
      onMobileScroll() {
        if (!this.mobileSlides || !this.mobileProgressBar) return;
        
        // Debounce to one update per frame to avoid jitter
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => {
          const scrollLeft = this.mobileSlides.scrollLeft;
          const slideWidth = this.mobileSlides.offsetWidth;
          const maxScroll = this.mobileSlides.scrollWidth - slideWidth;
          
          // Calculate current slide index (0-based)
          const currentSlideIndex = Math.round(scrollLeft / slideWidth);
          
          // Calculate the bar width as percentage (100% / totalSlides)
          const barWidthPercent = 100 / this.mobileTotalSlides;
          
          // Calculate translateX position: each slide moves the bar by its width
          const progress = maxScroll > 0 ? (scrollLeft / maxScroll) : 0;
          const leftPercent = progress * (100 - barWidthPercent);
          
          // Convert container-relative % to element-relative % for translateX
          const translatePercent = barWidthPercent > 0 ? (leftPercent / barWidthPercent * 100) : 0;
          
          // Update progress bar position using transform (avoids layout recalc)
          this.mobileProgressBar.style.transform = `translateX(${translatePercent}%)`;
          
          // Update counter only when slide actually changes
          if (currentSlideIndex !== this.mobileCurrentSlide) {
            this.mobileCurrentSlide = currentSlideIndex;
            if (this.mobileCounterCurrent) {
              this.mobileCounterCurrent.textContent = currentSlideIndex + 1;
            }
          }
        });
      }
      
      onMobileZoomClick() {
        // Get current slide's media ID and open modal
        const currentSlide = this.mobileSlidesArray[this.mobileCurrentSlide];
        if (!currentSlide) return;
        
        const mediaId = currentSlide.dataset.mediaId;
        const slideIndex = parseInt(currentSlide.dataset.slideIndex, 10) || 0;
        
        this.openModal(mediaId, slideIndex);
      }
    }
  );
}
